import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { LatLngTuple, latLngBounds } from 'leaflet';
import { Icon } from "leaflet";
import { useEffect, useState } from 'react';

import Button from 'react-bootstrap/Button';
import Nav from 'react-bootstrap/Nav';

import NewMarkerModal from './components/NewMarkerModal';
import { Coordinates, Location } from './types/location';
import { getAllLocations, getVersion } from './api/locationService';
import { useGeolocation } from './hooks/useGeolocation';
import { useMarkerModal } from './hooks/useMarkerModal';

const icon = new Icon({
    iconUrl: "/markers/ducato.png",
    iconSize: [35, 35]
})

function MyMap() {
    const center: LatLngTuple = [49.571662902832, 10.8264169692993]

    const [markers, setMarkers] = useState<Location[]>([]);
    const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
    const [doFitBounds, setDoFitBounds] = useState(true);
    const [version, setVersion] = useState<string>("");

    const { show, showModal, closeModal, handleCommentUpdate, editMarker, deleteMarker, initialComment } = useMarkerModal(markers, setMarkers);
    const { createCurrentPositionMarker } = useGeolocation(markers, setMarkers, setCurrentLocation, showModal);

    useEffect(() => {
        getAllLocations()
            .then(setMarkers)
            .catch(console.error);
        getVersion()
            .then(setVersion)
            .catch(console.error);
    }, []);

    return (
        <>
            <Nav
                activeKey="/home"
                onSelect={(selectedKey) => alert(`selected ${selectedKey}`)}
            >
                <Nav.Item>
                    <Button className="m-3" onClick={createCurrentPositionMarker}>Get Current Location</Button>
                </Nav.Item>
                <Nav.Item>
                    <Button className="m-3" onClick={() => setDoFitBounds(true)}>Zoom out</Button>
                </Nav.Item>
                {version && (
                    <Nav.Item className="ms-auto">
                        <span className="m-3 text-muted" style={{ fontSize: '0.8rem', lineHeight: '38px' }}>{version}</span>
                    </Nav.Item>
                )}
            </Nav>

            <MapContainer
                center={center}
                zoom={15}
                scrollWheelZoom={true} >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MyMarkers markers={markers} currentLocation={currentLocation} doFitBounds={doFitBounds} setDoFitBounds={setDoFitBounds} onEdit={editMarker} onDelete={deleteMarker} />
                <ManualMarker markers={markers} setMarkers={setMarkers} showModal={showModal} />
            </MapContainer>

            <NewMarkerModal
                show={show}
                onHide={closeModal}
                onCommentUpdate={handleCommentUpdate}
                initialComment={initialComment}
            />
        </>
    );
}

function ManualMarker({ markers, setMarkers, showModal }: {
    markers: Location[],
    setMarkers: (markers: Location[]) => void
    showModal: (coordsInModification: Coordinates) => void
}) {
    useMapEvents({
        click(e) {
            const { lat, lng } = e.latlng;
            setMarkers([...markers, { latitude: lat, longitude: lng } as Location]);
            showModal({ lat, long: lng } as Coordinates);
        }
    });

    return null;
}

function MyMarkers({ markers, currentLocation, doFitBounds, setDoFitBounds, onEdit, onDelete }: {
    markers: Location[],
    currentLocation?: Location | null,
    doFitBounds: boolean,
    setDoFitBounds: (doFitBounds: boolean) => void,
    onEdit: (location: Location) => void,
    onDelete: (id: number) => void,
}) {
    const map = useMap();

    useEffect(() => {
        if (currentLocation) {
            map.flyTo([currentLocation.latitude, currentLocation.longitude], 13);
        }
    }, [currentLocation, map]);

    useEffect(() => {
        if (doFitBounds) {
            const bounds = latLngBounds([]);
            for (let i = 0; i < markers.length; i++) {
                bounds.extend([markers[i].latitude, markers[i].longitude]);
            }
            if (bounds.isValid()) {
                map.fitBounds(bounds);
                setDoFitBounds(false);
            }
        }
    }, [markers, map, doFitBounds, setDoFitBounds]);

    return (
        <>
            {markers.map((h) => (
                <Marker key={h.id ?? `${h.latitude}-${h.longitude}`} position={[h.latitude, h.longitude]} icon={icon}>
                    <Popup>
                        <div>
                            <strong>Koordinaten</strong>
                            <br />
                            <a
                                href={`https://www.google.com/maps?q=${h.latitude},${h.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {h.latitude.toFixed(6)}, {h.longitude.toFixed(6)}
                            </a>
                        </div>
                        {h.comment && (
                            <div style={{ marginTop: '8px' }}>
                                <strong>Beschreibung</strong>
                                <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{h.comment}</p>
                            </div>
                        )}
                        {h.id && (
                            <div style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
                                <Button size="sm" variant="outline-primary" onClick={() => onEdit(h)}>Bearbeiten</Button>
                                <Button size="sm" variant="outline-danger" onClick={() => onDelete(h.id!)}>Löschen</Button>
                            </div>
                        )}
                    </Popup>
                </Marker>
            ))}
        </>
    );
}

export default MyMap;